import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import useConfirm from "@/hooks/useConfirm";
import { usersKeys } from "@/query-keys";
import { type UserListItem } from "@/schemas/userSchema";
import { services } from "@/services";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Trash2, XCircle } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CreateUserForm from "./_components/CreateUserForm";
import useColumns from "./_hooks/useColumns";
import useUserMutation from "./_hooks/useUserMutation";
import useUsersQuery from "./_hooks/useUsersQuery";

export const Route = createFileRoute("/_private/users/")({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.isRoot) {
      throw redirect({ to: "/stats" });
    }
  },
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: usersKeys.lists(deps.q),
      queryFn: () => services.getUsers(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = useUsersQuery(search.q, loaderData);

  const columns = useColumns();
  const { deleteUser, isDeleting, toggleActiveStatus, isToggling } = useUserMutation();

  const deleteConfirm = useConfirm({
    title: t("users.confirm.delete.title"),
    description: t("users.confirm.delete.description"),
    isPending: isDeleting,
  });

  const toggleConfirm = useConfirm({
    title: t("users.confirm.deactivate.title"),
    description: t("users.confirm.deactivate.description"),
    variant: "default",
    isPending: isToggling,
  });

  const handleView = useCallback(
    (row: UserListItem) => navigate({ to: "/users/$userId", params: { userId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: UserListItem) => {
      deleteConfirm.confirm(() => {
        deleteUser(row.id);
      });
    },
    [deleteConfirm, deleteUser],
  );

  const actions = useMemo(
    () => [
      {
        icon: (row: UserListItem) =>
          row.isActive ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />,
        label: (row: UserListItem) =>
          row.isActive ? t("common.deactivate") : t("common.activate"),
        handler: (row: UserListItem) =>
          toggleConfirm.confirm(() => {
            toggleActiveStatus(row.id);
          }),
        disabled: (row: UserListItem) => row.deletedAt != null || row.isRoot,
      },
      {
        icon: <Trash2 className="h-4 w-4 text-destructive" />,
        label: <span className="text-destructive">{t("common.delete")}</span>,
        handler: handleDelete,
        disabled: (row: UserListItem) => row.deletedAt != null || row.isRoot,
      },
    ],
    [t, toggleConfirm, toggleActiveStatus, handleDelete],
  );

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader
        title={t("users.list.title")}
        subtitle={t("users.list.subtitle")}
        action={<CreateUserForm />}
      />

      <DataTable<UserListItem>
        searchPlaceholder={t("users.list.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={usersKeys.lists(search.q)}
        navigateFrom="/users"
        searchFrom="/_private/users/"
        onView={handleView}
        actions={actions}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
      {toggleConfirm.confirmDialog}
    </div>
  );
}
